import { useProjectStore } from "../state/projectStore";
import { useKeyStore } from "../state/keyStore";
import { useStyleStore } from "../state/styleStore";
import { useChordView } from "./useChordView";
import { measureSegments } from "../core/grid/segments";
import { barreCount, type ChordView } from "../core/theory/transpose";
import { shapeKey, type DroneId } from "../core/theory/voicings";
import { chartDrone, styleChart, styleOptions, type PlayStyle, type StyleOption } from "../core/theory/styles";
import type { ChartMeasure } from "../types/domain";

/** a chord as shown: spelled for the key on screen (ASCII ♯/♭ like ChordView.name) */
export interface ShownChord {
  key: string;            // identity for the shape strip: name + fingering
  name: string;
  added: string;          // trailing part the play style added, drawn in accent
  from: string | null;    // the analyzed chord when the name no longer shows it
  frets: number[];
  marks: number[];
}

export interface StyledChart {
  /** the style in effect — "orig" when the chosen one doesn't fit the current key/capo */
  style: PlayStyle;
  options: StyleOption[];
  drone: DroneId | null;
  barre: number;          // barre chords in the plain chart at this setting (the easy mode's wording)
  at: (measure: number, segment: number) => ShownChord | null;
  /** distinct fingerings in order of first appearance */
  diagrams: ShownChord[];
}

let cache: { deps: unknown[]; value: StyledChart } | null = null;

function build(measures: ChartMeasure[], key: { tonic: string; quality: "major" | "minor" }, transpose: number, capo: number,
  chosen: PlayStyle, view: ChordView): StyledChart {
  const flat: { m: number; s: number; label: string | null }[] = [];
  measures.forEach((m, mi) => measureSegments(m).forEach((seg, si) => flat.push({ m: mi, s: si, label: seg.chord })));
  const sounding = flat.map((f) => f.label);
  const options = styleOptions(sounding, key, { transpose, capo });
  const style = options.some((o) => o.style === chosen && o.capo == null) ? chosen : "orig";
  const shapes = sounding.map((l) => (l ? view.shape(l) : null));
  const sk = shapeKey(key, view.shift);
  const played = styleChart(shapes, sk, style);

  const index = new Map<string, ShownChord | null>();
  const diagrams: ShownChord[] = [];
  const seen = new Set<string>();
  played.forEach((p, i) => {
    let shown: ShownChord | null = null;
    if (p) {
      const name = view.spell(p.name);
      const base = view.spell(p.name.slice(0, p.name.length - p.added.length));
      shown = {
        key: name + "|" + p.frets.join(","), name,
        added: p.added && name.startsWith(base) ? name.slice(base.length) : "",
        from: p.from ? view.spell(p.from) : null, frets: p.frets, marks: p.marks,
      };
      if (!seen.has(shown.key)) { seen.add(shown.key); diagrams.push(shown); }
    }
    index.set(flat[i].m + ":" + flat[i].s, shown);
  });
  return {
    style, options,
    drone: style === "drone" ? chartDrone(shapes, sk) : null,
    barre: barreCount([...new Set(sounding.filter((l): l is string => !!l))], view.shift),
    at: (m, s) => index.get(m + ":" + s) ?? null,
    diagrams,
  };
}

/** the chord chart as the chosen play style plays it at the current key/capo (shared by grid, dock and exports) */
export function useStyledChart(): StyledChart {
  const measures = useProjectStore((s) => s.chartMeasures);
  const key = useProjectStore((s) => s.analysis?.key);
  const transpose = useKeyStore((s) => s.transpose);
  const capo = useKeyStore((s) => s.capo);
  const chosen = useStyleStore((s) => s.style);
  const view = useChordView();
  const k = key ? { tonic: key.tonic, quality: key.quality } : { tonic: "C", quality: "major" as const };
  const deps = [measures, k.tonic, k.quality, transpose, capo, chosen, view.shift];
  if (!cache || deps.some((d, i) => !Object.is(d, cache!.deps[i]))) cache = { deps, value: build(measures, k, transpose, capo, chosen, view) };
  return cache.value;
}
